/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Multimodal Chat Input Box & Voice STT Recording (Task 10.3.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  ChatAttachmentService,
  type ChatAttachmentItem,
} from '../../services/chat-attachment.service';
import {
  WebAudioRecorderService,
  WhisperTranscriptionService,
  type AudioRecordingState,
} from '../../services/voice-recorder.service';
import { useI18n } from '../../hooks/useI18n';
import {
  Paperclip,
  Mic,
  Send,
  X,
  FileText,
  FileCode,
  FileSpreadsheet,
  File,
  Sparkles,
  Loader2,
  StopCircle,
  AlertCircle,
  Database,
} from 'lucide-react';

export interface MultimodalMessagePayload {
  content: string;
  attachments: ChatAttachmentItem[];
}

interface MultimodalChatInputProps {
  onSendMessage: (payload: MultimodalMessagePayload) => void;
  onOpenTemplates?: () => void;
  placeholder?: string;
  disabled?: boolean;
  modelName?: string;
  creditCost?: number;
  className?: string;
}

export const MultimodalChatInput: React.FC<MultimodalChatInputProps> = ({
  onSendMessage,
  onOpenTemplates,
  placeholder,
  disabled = false,
  modelName = 'GPT-4o',
  creditCost = 1,
  className = '',
}) => {
  const { t } = useI18n();
  const effectivePlaceholder = placeholder || t('chat.inputPlaceholder');
  const [content, setContent] = useState('');
  const [attachments, setAttachments] = useState<ChatAttachmentItem[]>([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);

  // Audio Recording State
  const [recordingState, setRecordingState] = useState<AudioRecordingState>({
    isRecording: false,
    isProcessing: false,
    durationSeconds: 0,
    formattedDuration: '00:00',
    waveformLevels: new Array(16).fill(0.1),
  });

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const recorderRef = useRef<WebAudioRecorderService | null>(null);

  // Initialize Web Audio Recorder instance
  useEffect(() => {
    recorderRef.current = new WebAudioRecorderService((newState) => {
      setRecordingState(newState);
    });
    return () => {
      if (recorderRef.current) {
        recorderRef.current.cancelRecording();
      }
    };
  }, []);

  // Auto-resize textarea height between 42px (1 line) and 180px (~8 lines)
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const newHeight = Math.min(Math.max(textarea.scrollHeight, 42), 180);
    textarea.style.height = `${newHeight}px`;
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [content, adjustTextareaHeight]);

  // Handle message sending
  const handleSend = () => {
    const trimmed = content.trim();
    if ((!trimmed && attachments.length === 0) || disabled || recordingState.isRecording) {
      return;
    }

    onSendMessage({
      content: trimmed,
      attachments,
    });

    setContent('');
    setAttachments([]);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  // Keyboard shortcut handler
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle paste events (e.g. screenshots from clipboard)
  const handlePaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pastedAttachments = await ChatAttachmentService.handlePasteEvent(e);
    if (pastedAttachments.length > 0) {
      e.preventDefault();
      setAttachments((prev) => [...prev, ...pastedAttachments]);
    }
  };

  // Handle file input selection
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newAttachments = await ChatAttachmentService.processMultipleFiles(files);
    setAttachments((prev) => [...prev, ...newAttachments]);

    // Reset input value so same file can be selected again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOver(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const dropped = await ChatAttachmentService.processMultipleFiles(e.dataTransfer.files);
      setAttachments((prev) => [...prev, ...dropped]);
    }
  };

  // Remove an attachment item
  const removeAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  // Start microphone recording
  const handleStartVoiceRecord = async () => {
    if (!recorderRef.current) return;
    try {
      await recorderRef.current.startRecording();
    } catch {
      // Error handled in service state
    }
  };

  // Stop recording and transcribe with Whisper STT
  const handleStopAndTranscribe = async () => {
    if (!recorderRef.current) return;
    try {
      setIsTranscribing(true);
      const audioBlob = await recorderRef.current.stopRecording();
      const transcribedText = await WhisperTranscriptionService.transcribeAudio(audioBlob);

      if (transcribedText) {
        setContent((prev) => (prev ? `${prev} ${transcribedText}` : transcribedText));
      }
    } catch (err) {
      console.error('[MultimodalChatInput] Transcription error:', err);
    } finally {
      setIsTranscribing(false);
    }
  };

  // Cancel voice recording
  const handleCancelVoiceRecord = () => {
    if (recorderRef.current) {
      recorderRef.current.cancelRecording();
    }
  };

  // Helper to get doc icon
  const getDocIcon = (name: string) => {
    if (name.endsWith('.csv') || name.endsWith('.xlsx')) return FileSpreadsheet;
    if (name.endsWith('.json') || name.endsWith('.ts') || name.endsWith('.js')) return FileCode;
    if (name.endsWith('.pdf') || name.endsWith('.docx') || name.endsWith('.txt')) return FileText;
    return File;
  };

  const hasAttachments = attachments.length > 0;
  const canSend = (content.trim().length > 0 || hasAttachments) && !disabled && !recordingState.isRecording;

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`relative w-full rounded-2xl border transition-all duration-200 bg-surface shadow-sm ${
        isDraggingOver
          ? 'border-primary ring-2 ring-primary/30 bg-primary/5'
          : 'border-surface-border focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/20'
      } ${className}`}
    >
      {/* 1. Drag & Drop Visual Overlay */}
      {isDraggingOver && (
        <div className="absolute inset-0 z-30 flex items-center justify-center rounded-2xl bg-primary/10 backdrop-blur-sm border-2 border-dashed border-primary text-xs font-semibold text-primary animate-in fade-in duration-150">
          <Paperclip className="w-5 h-5 mr-2 animate-bounce" />
          <span>{t('chat.dropFilesHint')}</span>
        </div>
      )}

      {/* 2. Attachments Preview Tray */}
      {hasAttachments && (
        <div className="flex flex-wrap items-center gap-2.5 p-3 pb-1 border-b border-surface-border/60">
          {attachments.map((att) => {
            const isImage = att.type === 'image';
            const DocIcon = getDocIcon(att.name);

            return (
              <div
                key={att.id}
                className={`group relative flex items-center gap-2 p-1.5 pr-2.5 rounded-xl border text-xs transition-all ${
                  att.uploadStatus === 'error'
                    ? 'border-destructive/40 bg-destructive/5 text-destructive'
                    : 'border-surface-border bg-surface-elevated text-foreground hover:border-primary/30'
                }`}
              >
                {/* Image Thumbnail or Doc Icon */}
                {isImage && att.previewUrl ? (
                  <div className="relative w-9 h-9 rounded-lg overflow-hidden border border-surface-border bg-black/10 flex-shrink-0">
                    <img
                      src={att.previewUrl}
                      alt={att.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                    <DocIcon className="w-4 h-4" />
                  </div>
                )}

                <div className="flex flex-col min-w-0 max-w-[140px] sm:max-w-[180px]">
                  <span className="truncate text-[11px] font-semibold leading-tight">
                    {att.name}
                  </span>
                  <div className="flex items-center gap-1.5 text-[10px] text-foreground-muted">
                    <span>{att.formattedSize}</span>
                    {att.type === 'document' && (
                      <span className="inline-flex items-center gap-0.5 text-accent font-medium">
                        <Database className="w-2.5 h-2.5" />
                        RAG
                      </span>
                    )}
                  </div>
                </div>

                {att.uploadStatus === 'error' && (
                  <span title={att.errorMessage} className="flex-shrink-0">
                    <AlertCircle className="w-3.5 h-3.5 text-destructive" />
                  </span>
                )}

                {/* Remove button */}
                <button
                  type="button"
                  onClick={() => removeAttachment(att.id)}
                  title={t('chat.removeFile')}
                  className="p-1 rounded-full text-foreground-muted hover:text-foreground hover:bg-muted/80 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* 3. Input Body: Recording Mode vs Text Input Mode */}
      {recordingState.isRecording || isTranscribing ? (
        /* Live Audio Waveform Recording Strip */
        <div className="flex items-center justify-between p-3.5 px-4 bg-primary/5 rounded-2xl animate-in fade-in duration-200">
          <div className="flex items-center gap-3">
            {/* Blinking Red Dot */}
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-destructive animate-ping" />
              <span className="text-xs font-bold text-destructive">
                {isTranscribing ? t('chat.whisperTranscribing') : t('chat.recording')}
              </span>
            </div>

            {/* Time Counter */}
            <span className="font-mono text-xs font-semibold text-foreground px-2 py-0.5 rounded-md bg-surface border border-surface-border">
              {recordingState.formattedDuration}
            </span>

            {/* Real-time Waveform Equalizer Bars */}
            {!isTranscribing && (
              <div className="flex items-center gap-1 h-6 px-2">
                {recordingState.waveformLevels.map((level, idx) => (
                  <span
                    key={idx}
                    className="w-1 bg-primary rounded-full transition-all duration-75"
                    style={{
                      height: `${Math.max(4, Math.round(level * 24))}px`,
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {isTranscribing ? (
              <div className="flex items-center gap-2 text-xs text-primary font-medium">
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
                <span>Whisper STT</span>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleCancelVoiceRecord}
                  title={t('chat.cancelRecording')}
                  className="p-2 rounded-xl text-foreground-muted hover:text-destructive hover:bg-destructive/10 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={handleStopAndTranscribe}
                  title={t('chat.stopAndInsertBtn')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary text-primary-fg text-xs font-semibold hover:opacity-90 shadow-sm transition-opacity"
                >
                  <StopCircle className="w-3.5 h-3.5" />
                  <span>{t('chat.stopAndInsert')}</span>
                </button>
              </>
            )}
          </div>
        </div>
      ) : (
        /* Standard Multimodal Textarea */
        <div className="p-2.5 sm:p-3">
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={effectivePlaceholder}
            disabled={disabled}
            rows={1}
            className="w-full resize-none p-1.5 bg-transparent text-xs text-foreground placeholder:text-foreground-muted/60 focus:outline-none scrollbar-none leading-relaxed"
          />

          {/* Bottom Action Utilities Bar */}
          <div className="flex items-center justify-between pt-2 border-t border-surface-border/40 text-xs">
            {/* Left Controls: File Attachment, Audio Mic, Templates */}
            <div className="flex items-center gap-1">
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*, .pdf, .docx, .txt, .csv, .md, .json"
                onChange={handleFileChange}
                className="hidden"
              />

              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                title={t('chat.attachFiles')}
                className="p-2 rounded-xl text-foreground-muted hover:text-foreground hover:bg-muted/80 transition-colors"
              >
                <Paperclip className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={handleStartVoiceRecord}
                title={t('chat.recordVoiceTooltip')}
                className="p-2 rounded-xl text-foreground-muted hover:text-primary hover:bg-primary/10 transition-colors"
              >
                <Mic className="w-4 h-4" />
              </button>

              {onOpenTemplates && (
                <button
                  type="button"
                  onClick={onOpenTemplates}
                  title={t('chat.openPromptTemplates')}
                  className="p-2 rounded-xl text-foreground-muted hover:text-accent hover:bg-accent/10 transition-colors"
                >
                  <Sparkles className="w-4 h-4 text-accent" />
                </button>
              )}
            </div>

            {/* Right Controls: Model Credits Pill & Send Button */}
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-foreground-muted hidden sm:inline-block">
                {modelName} • {creditCost} {t('common.credits')}
              </span>

              <button
                type="button"
                onClick={handleSend}
                disabled={!canSend}
                title={`${t('chat.sendMessage')} (Enter)`}
                className="p-2 rounded-xl bg-primary text-primary-fg hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95 shadow-sm"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recording error alert if mic failed */}
      {recordingState.errorMessage && (
        <div className="p-2 px-3 text-[11px] text-destructive bg-destructive/10 rounded-b-2xl flex items-center justify-between">
          <span>{recordingState.errorMessage}</span>
          <button
            type="button"
            onClick={() => setRecordingState((prev) => ({ ...prev, errorMessage: undefined }))}
            className="text-xs font-bold hover:underline"
          >
            {t('common.close')}
          </button>
        </div>
      )}
    </div>
  );
};
